/**
 * useFormValidation Hook
 *
 * UX15: Immediate form validation feedback with smart timing.
 *
 * Validation Strategy:
 * - On first interaction: Validate on blur (after user leaves field)
 * - After first validation: Validate on change (immediate feedback)
 * - Async validation: Debounced (500ms) to avoid excessive API calls
 *
 * Psychology: Immediate feedback reduces cognitive load and prevents
 * form abandonment by showing users errors as soon as possible.
 *
 * @example
 * ```typescript
 * const { values, errors, touched, handleChange, handleBlur, isValid } = useFormValidation({
 *   initialValues: { email: '', password: '' },
 *   validate: (values) => {
 *     const errors: Record<string, string> = {}
 *     if (!values.email) errors.email = 'Email is required'
 *     if (!values.password) errors.password = 'Password is required'
 *     return errors
 *   },
 * })
 * ```
 */

import { useState, useCallback, useRef, useEffect } from 'react'

export interface UseFormValidationOptions<T> {
  /** Initial form values */
  initialValues: T
  /** Validation function that returns errors object */
  validate: (values: T) => Record<string, string> | Promise<Record<string, string>>
  /** Debounce time for validation in ms (default: 300ms) */
  debounceMs?: number
  /** Validate on mount (default: false) */
  validateOnMount?: boolean
}

export interface UseFormValidationReturn<T> {
  /** Current form values */
  values: T
  /** Validation errors */
  errors: Record<string, string>
  /** Fields that have been touched (blurred at least once) */
  touched: Record<string, boolean>
  /** Fields currently being validated asynchronously */
  validating: Record<string, boolean>
  /** Whether the entire form is valid */
  isValid: boolean
  /** Whether any field is being validated */
  isValidating: boolean
  /** Whether the form has been touched */
  isTouched: boolean
  /** Update a single field value */
  setFieldValue: (field: keyof T, value: any) => void
  /** Update multiple field values */
  setValues: (values: Partial<T>) => void
  /** Mark a field as touched */
  setFieldTouched: (field: keyof T, touched: boolean) => void
  /** Set an error for a field */
  setFieldError: (field: keyof T, error: string) => void
  /** Clear all errors */
  clearErrors: () => void
  /** Reset form to initial values */
  reset: () => void
  /** Handle input change event */
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
  /** Handle input blur event */
  handleBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
  /** Validate all fields */
  validateForm: () => Promise<boolean>
  /** Validate a single field */
  validateField: (field: keyof T) => Promise<void>
}

export function useFormValidation<T extends Record<string, any>>({
  initialValues,
  validate,
  debounceMs = 300,
  validateOnMount = false,
}: UseFormValidationOptions<T>): UseFormValidationReturn<T> {
  const [values, setValuesState] = useState<T>(initialValues)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [validating, setValidating] = useState<Record<string, boolean>>({})

  // Track validation timers for debouncing
  const validationTimers = useRef<Record<string, NodeJS.Timeout>>({})

  // Track if we've ever validated (for smart validation timing)
  const hasValidatedRef = useRef<Record<string, boolean>>({})

  // Validate on mount if requested
  useEffect(() => {
    if (validateOnMount) {
      validateForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Validate all fields
   */
  const validateForm = useCallback(async (): Promise<boolean> => {
    try {
      const validationErrors = await validate(values)
      setErrors(validationErrors)
      return Object.keys(validationErrors).length === 0
    } catch (error) {
      console.error('Form validation error:', error)
      return false
    }
  }, [values, validate])

  /**
   * Validate a single field with debouncing
   */
  const validateField = useCallback(
    async (field: keyof T): Promise<void> => {
      // Clear existing timer for this field
      if (validationTimers.current[field as string]) {
        clearTimeout(validationTimers.current[field as string])
      }

      // Set validating state
      setValidating((prev) => ({ ...prev, [field]: true }))

      // Debounce validation
      return new Promise((resolve) => {
        validationTimers.current[field as string] = setTimeout(async () => {
          try {
            const validationErrors = await validate(values)
            setErrors((prev) => ({
              ...prev,
              [field]: validationErrors[field as string] || '',
            }))
            hasValidatedRef.current[field as string] = true
          } catch (error) {
            console.error(`Field validation error for ${String(field)}:`, error)
          } finally {
            setValidating((prev) => ({ ...prev, [field]: false }))
            resolve()
          }
        }, debounceMs)
      })
    },
    [values, validate, debounceMs]
  )

  /**
   * Handle input change
   * - Validates immediately if field has been touched and validated before
   * - Otherwise waits for blur
   */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target

      // Handle checkbox
      const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value

      setValuesState((prev) => ({ ...prev, [name]: newValue }))

      // If field has been touched and validated before, validate on change
      if (touched[name] && hasValidatedRef.current[name]) {
        validateField(name as keyof T)
      }
    },
    [touched, validateField]
  )

  /**
   * Handle input blur
   * - Always validates on blur (first-time or subsequent)
   */
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name } = e.target

      // Mark field as touched
      setTouched((prev) => ({ ...prev, [name]: true }))

      // Validate on blur
      validateField(name as keyof T)
    },
    [validateField]
  )

  /**
   * Set a single field value
   */
  const setFieldValue = useCallback(
    (field: keyof T, value: any) => {
      setValuesState((prev) => ({ ...prev, [field]: value }))

      // If field has been touched, validate immediately
      if (touched[field as string] && hasValidatedRef.current[field as string]) {
        validateField(field)
      }
    },
    [touched, validateField]
  )

  /**
   * Set multiple field values
   */
  const setValues = useCallback((newValues: Partial<T>) => {
    setValuesState((prev) => ({ ...prev, ...newValues }))
  }, [])

  /**
   * Mark a field as touched
   */
  const setFieldTouched = useCallback((field: keyof T, isTouched: boolean) => {
    setTouched((prev) => ({ ...prev, [field]: isTouched }))
  }, [])

  /**
   * Set an error for a field
   */
  const setFieldError = useCallback((field: keyof T, error: string) => {
    setErrors((prev) => ({ ...prev, [field]: error }))
  }, [])

  /**
   * Clear all errors
   */
  const clearErrors = useCallback(() => {
    setErrors({})
  }, [])

  /**
   * Reset form to initial values
   */
  const reset = useCallback(() => {
    setValuesState(initialValues)
    setErrors({})
    setTouched({})
    setValidating({})
    hasValidatedRef.current = {}

    // Clear all timers
    Object.values(validationTimers.current).forEach(clearTimeout)
    validationTimers.current = {}
  }, [initialValues])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(validationTimers.current).forEach(clearTimeout)
    }
  }, [])

  // Computed values
  const isValid = Object.keys(errors).length === 0 && Object.keys(errors).every((key) => !errors[key])
  const isValidating = Object.values(validating).some((v) => v)
  const isTouched = Object.keys(touched).length > 0

  return {
    values,
    errors,
    touched,
    validating,
    isValid,
    isValidating,
    isTouched,
    setFieldValue,
    setValues,
    setFieldTouched,
    setFieldError,
    clearErrors,
    reset,
    handleChange,
    handleBlur,
    validateForm,
    validateField,
  }
}
